import { useCallback, useEffect, useState } from 'react'

import { useApplicationStore } from 'app/application/infrastructure'
import { useApi } from 'app/common/application'

import {
  AvailableOrInstalledConnection,
  ConnectionConfig,
  ConnectionDeleteRequest,
  ConnectionDeleteResponse,
  ConnectionName,
  ConnectionsGetRequest,
  ConnectionsGetResponse,
  InstalledConnection,
  isInstalledConnection,
  mapToConnections,
} from '../types'
import { availableConnections } from './availableConnections'
import { saveConnection as _saveConnection } from './saveConnection'

const useConnections = () => {
  const { create } = useApi()
  const connectionsData = useApplicationStore((state) => state.connections)
  const addConnectionData = useApplicationStore((state) => state.addConnection)
  const deleteConnectionData = useApplicationStore((state) => state.deleteConnection)
  const setConnectionsData = useApplicationStore((state) => state.setConnections)
  const updateConnectionData = useApplicationStore((state) => state.updateConnection)
  const [connections, setConnections] = useState<InstalledConnection[]>(
    mapToConnections(connectionsData, availableConnections),
  )

  useEffect(
    () =>
      setConnections((prev) => {
        const updatedConnection = mapToConnections(connectionsData, availableConnections)
        return JSON.stringify(prev) === JSON.stringify(updatedConnection) ? prev : updatedConnection
      }),
    [connectionsData],
  )

  const deleteConnection = useCallback(
    async (conn: InstalledConnection) => {
      const result = await create<ConnectionDeleteRequest, ConnectionDeleteResponse>(
        '/connections/delete',
        {
          content: {
            id: conn.id,
          },
        },
      )
      if (result.ok) {
        deleteConnectionData(conn.id)
      }

      return result
    },
    [create, deleteConnectionData],
  )
  const fetchConnections = useCallback(async () => {
    const result = await create<ConnectionsGetRequest, ConnectionsGetResponse>(
      '/connections/query',
      {
        content: {},
      },
    )
    if (result.ok) {
      setConnectionsData(result.val.data)
    }
  }, [create, setConnectionsData])

  const getConnection = useCallback(
    (name: ConnectionName) =>
      connections.find((conn) => conn.name === name) ??
      availableConnections.find((availableConn) => availableConn.name === name) ??
      null,

    [connections],
  )

  const saveConnection = useCallback(
    async (
      conn: AvailableOrInstalledConnection,
      connConfig: ConnectionConfig,
      authorizationCode?: string,
      redirectUri?: string,
    ) => {
      const result = await _saveConnection(create, conn, connConfig, authorizationCode, redirectUri)
      if (result.ok) {
        if (isInstalledConnection(conn)) {
          updateConnectionData(result.val)
        } else {
          addConnectionData(result.val)
        }
      }

      return result
    },
    [create, updateConnectionData, addConnectionData],
  )

  return {
    availableConnections,
    deleteConnection,
    fetchConnections,

    getConnection,
    saveConnection,
    connections,
  }
}

export default useConnections
